import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { withReturnTo } from '../lib/returnTo';

/** ログイン等の認証系画面は、未ログインでもそのまま表示する必要があるパス。 */
const AUTH_PATH_PREFIXES = ['/login', '/signup', '/verify', '/reset'];

/**
 * dev 環境限定で、管理者（Cognito グループ 'admins'）以外はサイト全体を弾くゲート。
 * VITE_REQUIRE_LOGIN='true' のビルド（develop）でのみ有効化し、モックモード
 * （authEnabled=false）では AuthContext が常に isAdmin: true の LOCAL_USER を返すため
 * 自然に通過する。
 */
export default function RequireLoginGate({ children }: { children: React.ReactNode }) {
  const requireLogin = import.meta.env.VITE_REQUIRE_LOGIN === 'true';
  const { user, ready } = useAuth();
  const location = useLocation();

  if (!requireLogin) return <>{children}</>;

  // トークン復元中に一瞬でも保護コンテンツが見えないよう、準備完了まで何も描画しない。
  if (!ready) return null;

  if (!user) {
    // ログイン画面自体はブロックしない（ここを弾くとログインできなくなる）。
    if (AUTH_PATH_PREFIXES.some((prefix) => location.pathname.startsWith(prefix))) {
      return <>{children}</>;
    }
    // state に加えクエリにも from を載せる（新規登録を挟む・リロードされる経路でも失われないよう）。
    const from = location.pathname + location.search;
    return <Navigate to={withReturnTo('/login', from)} state={{ from }} replace />;
  }

  // ログイン済みでも管理者以外は /no-access へ。無限リダイレクトを避けるため、
  // 既に /no-access にいる場合はそのまま表示する。認証系パスも通さない
  // （/no-access からログアウトすれば未ログイン状態になり /login には入れる）。
  if (!user.isAdmin) {
    if (location.pathname.startsWith('/no-access')) return <>{children}</>;
    return <Navigate to="/no-access" replace />;
  }

  return <>{children}</>;
}
